// @ts-strict-ignore
import Text from 'antd/lib/typography/Text';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { selectAppSettings } from '../../selectors/selectors';
import { antGrey, runwayColors } from '../styles/globalStyles';
import Title from 'antd/lib/typography/Title';
import { appNameWithPlatform, platformLabelForAppName } from '../../helpers/mdmPlatformOverride';
import appPlaceholderImage from '../../../assets/app-placeholder.png';
import Icon, { RocketOutlined, ThunderboltOutlined, ArrowRightOutlined, MobileOutlined } from '@ant-design/icons';
import { createAppBuildsRoute, createAppRoute, createAutomationsToolboxRoute } from '../../helpers/routeHelpers';
import { useHistory } from 'react-router-dom';
import { AppComponentProps } from '../../typescriptHelpers';
import * as Analytics from '../../utils/analytics';
import TrackedButton from '../../helpers/TrackedButton';
import Layout from 'antd/lib/layout';
import Space from 'antd/lib/space';
import Avatar from 'antd/lib/avatar';
import Divider from 'antd/lib/divider';

export const AppHomeGetStartedComponent: React.FC<AppComponentProps> = ({ appId, orgId }) => {
    const history = useHistory();
    const appSettings = useSelector((state: RootState) => selectAppSettings(state, appId));
    const appImageUrl = useSelector((state: RootState) => selectAppSettings(state, appId).appImageUrl);

    return (
        <Layout>
            <div style={{ padding: '80px 100px' }}>
                <Space size={35} direction="vertical" style={{ width: '800px' }}>
                    <Space size={26}>
                        <Avatar
                            style={{
                                borderRadius: 12,
                                minWidth: 64,
                                filter: `drop-shadow(rgba(0, 0, 0, 0.2) 0px 4px 5px)`,
                            }}
                            src={appImageUrl || appPlaceholderImage}
                            size={64}
                        />
                        <Title
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                marginBottom: 0,
                                fontSize: 24,
                                fontWeight: 600,
                            }}
                        >
                            <span>
                                How would you like to use Runway with &nbsp;
                                {
                                    <span style={{ color: runwayColors.info }}>
                                        {appNameWithPlatform(appSettings.appName, platformLabelForAppName(appSettings))}
                                    </span>
                                }
                                ?
                            </span>
                        </Title>
                    </Space>
                    <div
                        style={{
                            borderStyle: 'solid',
                            borderColor: antGrey.grey5,
                            color: runwayColors.white,
                            borderWidth: 1,
                            borderRadius: '8px',
                            boxShadow: `4px 4px 0px rgba(0, 0, 0, 0.1)`,
                        }}
                    >
                        <Space
                            direction="vertical"
                            split={<Divider style={{ borderColor: antGrey.grey5 }} />}
                            size={0}
                            style={{
                                padding: '24px 36px 24px 46px',
                                width: '100%',
                            }}
                        >
                            <AppHomeGetStartedOption
                                orgId={orgId}
                                appId={appId}
                                title="Get started with releases"
                                description="Configure your workflow to set up your release process in Runway."
                                icon={RocketOutlined}
                                context={AppHomeGetStartedContext.Releases}
                                onClick={() => {
                                    const appRoute = createAppRoute(orgId, appId);
                                    history.push(appRoute);
                                }}
                            />
                            <AppHomeGetStartedOption
                                orgId={orgId}
                                appId={appId}
                                title="Get started with automations"
                                description="Browse helpful automations for key parts of your release workflow."
                                icon={ThunderboltOutlined}
                                context={AppHomeGetStartedContext.Automations}
                                onClick={() => {
                                    const automationsToolboxRoute = createAutomationsToolboxRoute(orgId, appId);
                                    history.push(automationsToolboxRoute);
                                }}
                            />
                            <AppHomeGetStartedOption
                                orgId={orgId}
                                appId={appId}
                                title="Get started with Build Distro"
                                description="Create and configure build buckets to share and install builds from Runway."
                                icon={MobileOutlined}
                                context={AppHomeGetStartedContext.BuildDistribution}
                                onClick={() => {
                                    const appBuildsRoute = createAppBuildsRoute(orgId, appId);
                                    history.push(appBuildsRoute);
                                }}
                            />
                        </Space>
                    </div>
                </Space>
            </div>
        </Layout>
    );
};

enum AppHomeGetStartedContext {
    Releases = 'releases',
    Automations = 'automations',
    BuildDistribution = 'build-distro',
}

interface AppHomeGetStartedOptionProps extends AppComponentProps {
    title: string;
    description: string;
    context: AppHomeGetStartedContext;
    icon: typeof RocketOutlined;
    onClick: () => void;
}

const AppHomeGetStartedOption: React.FC<AppHomeGetStartedOptionProps> = ({
    orgId,
    appId,
    title,
    description,
    icon,
    context,
    onClick,
}) => {
    return (
        <div
            style={{
                color: runwayColors.white,
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
            }}
        >
            <Space size={30}>
                <Icon component={icon} style={{ color: antGrey.grey5, fontSize: 60 }} />
                <Space direction="vertical" size={0}>
                    <Text style={{ fontSize: 20, fontWeight: 400, color: antGrey.grey9 }}>{title}</Text>
                    <Text style={{ fontSize: 14, fontWeight: 400, color: antGrey.grey7 }}>{description}</Text>
                </Space>
            </Space>
            <TrackedButton
                orgId={orgId}
                appId={appId}
                icon={<ArrowRightOutlined />}
                style={{
                    backgroundColor: runwayColors.primary,
                    color: 'white',
                    width: '78px',
                    height: '40px',
                    borderStyle: 'solid',
                    borderColor: runwayColors.primary,
                    borderWidth: 1,
                    borderRadius: '8px',
                    fontSize: '16px',
                }}
                onClick={onClick}
                eventName={Analytics.Event.ClickAppHomeGetStartedOption}
                extraProperties={{
                    optionType: context,
                }}
            >
                Go
            </TrackedButton>
        </div>
    );
};
